import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAdmin } from './hooks/useAdmin';
import { useStudioProfileClaimed } from './hooks/useStudioProfileClaimed';
import DancerThumbnail from './DancerThumbnail';
import ClaimStudioProfile from './ClaimStudioProfile';
import ManageStudioProfile from './ManageStudioProfile';
import LoginPrompt from './LoginPrompt';
import FollowButton from './components/FollowButton';
import './DancerProfile.css';

// Helper function to normalize Facebook URLs
const normalizeFacebookUrl = (facebookValue) => {
  if (!facebookValue) return '';
  
  // If it's already a complete URL, return as is
  if (facebookValue.startsWith('http')) {
    return facebookValue;
  }
  
  // Remove any existing facebook.com or www.facebook.com prefixes
  let cleanValue = facebookValue
    .replace(/^(https?:\/\/)?(www\.)?facebook\.com\//, '')
    .replace(/^facebook\.com\//, '');
  
  // Return properly formatted URL
  return `https://www.facebook.com/${cleanValue}`;
};

export default function StudioProfile() {
  const { studioName } = useParams();
  const [dancers, setDancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dancers');
  const [user, setUser] = useState(null);
  const [studioProfileData, setStudioProfileData] = useState(null);
  const [hasPendingClaim, setHasPendingClaim] = useState(false);
  const [visitorCount, setVisitorCount] = useState(null);
  const { isAdmin } = useAdmin();
  const { isProfileClaimed, claimedBy, loading: claimStatusLoading } = useStudioProfileClaimed(decodeURIComponent(studioName || ''));

  // Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Check for pending claim
  useEffect(() => {
    const checkPendingClaim = async () => {
      if (!user || !studioName) return;
      
      try {
        const decodedName = decodeURIComponent(studioName);
        const claimsRef = collection(db, 'studioProfileClaims');
        const q = query(
          claimsRef,
          where('studioName', '==', decodedName),
          where('userId', '==', user.uid),
          where('status', '==', 'pending')
        );
        
        const querySnapshot = await getDocs(q);
        setHasPendingClaim(!querySnapshot.empty);
      } catch (error) {
        console.error('Error checking pending claim:', error);
        setHasPendingClaim(false);
      }
    };
    
    checkPendingClaim();
  }, [user, studioName]);

  useEffect(() => {
    if (studioName) {
      const decodedName = decodeURIComponent(studioName);
      
      // Track profile visit
      const trackVisit = async () => {
        try {
          const functions = getFunctions();
          const trackProfileVisit = httpsCallable(functions, 'trackProfileVisit');
          const result = await trackProfileVisit({ 
            profileName: decodedName, 
            profileType: 'studio' 
          });
          
          // Update visitor count from the response
          if (result.data && result.data.totalVisits !== undefined) {
            setVisitorCount(result.data.totalVisits);
          }
        } catch (error) {
          // Silently fail - visit tracking shouldn't block profile loading
          console.log('Visit tracking error (non-critical):', error.message);
        }
      };
      
      trackVisit();
      loadStudioDancers(decodedName);
    }
  }, [studioName]);

  useEffect(() => {
    if (studioName && !claimStatusLoading) {
      const decodedName = decodeURIComponent(studioName);
      if (isProfileClaimed) {
        loadStudioProfileData(decodedName);
      }
    }
  }, [studioName, isProfileClaimed, claimStatusLoading]);

  // Load studio profile data if claimed
  async function loadStudioProfileData(studio) {
    if (!isProfileClaimed) return;
    
    try {
      console.log("📄 Loading studio profile data for:", studio);
      
      // First, try to get data from studioProfiles collection (primary source)
      const studioRef = doc(db, 'studioProfiles', studio);
      const studioSnap = await getDoc(studioRef);
      
      if (studioSnap.exists()) {
        const profileData = studioSnap.data();
        console.log("✅ Found studio profile data in studioProfiles:", profileData);
        console.log("🖼️ Logo URL found:", profileData.logoURL);
        setStudioProfileData(profileData);
        return;
      }
      
      // Fallback: get data from approved claim (for backwards compatibility)
      const claimsRef = collection(db, 'studioProfileClaims');
      const q = query(
        claimsRef, 
        where('studioName', '==', studio),
        where('status', '==', 'approved')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const claimData = querySnapshot.docs[0].data();
        console.log("✅ Found studio profile data in claims:", claimData);
        console.log("🖼️ Logo URL found:", claimData.logoURL);
        setStudioProfileData(claimData);
      } else {
        console.log("⚠️ No approved claim found for studio:", studio);
      }
    } catch (error) {
      console.error("❌ Error loading studio profile data:", error);
    }
  }

  async function loadStudioDancers(studio) {
    try {
      setLoading(true);
      setError(null);
      
      console.log("🏛️ Loading dancers for studio:", studio);
      
      const dancerMap = new Map();
      
      // Load from old structure
      const compsRef = collection(db, "competitions");
      const snapshot = await getDocs(compsRef);
      
      snapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.results && Array.isArray(comp.results)) {
          comp.results.forEach((result) => {
            if (result.studio === studio && result.dancerName) {
              const dancerName = result.dancerName;
              const place = result.place;
              
              if (!dancerMap.has(dancerName)) {
                dancerMap.set(dancerName, {
                  name: dancerName,
                  first: 0,
                  second: 0,
                  third: 0,
                  total: 0,
                  competitions: new Set()
                });
              }
              
              const dancer = dancerMap.get(dancerName);
              dancer.competitions.add(doc.id);
              
              if (place === 1) dancer.first++;
              else if (place === 2) dancer.second++;
              else if (place === 3) dancer.third++;
              
              if (place >= 1 && place <= 3) {
                dancer.total++;
              }
            }
          });
        }
      });
      
      // Load from new structure
      try {
        const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
        
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const disciplineName = disciplineDoc.id;
          const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
          
          for (const categoryDoc of categoriesSnapshot.docs) {
            const categoryName = categoryDoc.id;
            
            // Check category-level competitions
            try {
              const categoryCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
              
              categoryCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (comp.results && Array.isArray(comp.results)) {
                  comp.results.forEach((result) => {
                    if (result.studio === studio && result.dancerName) {
                      const dancerName = result.dancerName;
                      const place = result.place;
                      
                      if (!dancerMap.has(dancerName)) {
                        dancerMap.set(dancerName, {
                          name: dancerName,
                          first: 0,
                          second: 0,
                          third: 0,
                          total: 0,
                          competitions: new Set()
                        });
                      }
                      
                      const dancer = dancerMap.get(dancerName);
                      dancer.competitions.add(doc.id);
                      
                      if (place === 1) dancer.first++;
                      else if (place === 2) dancer.second++;
                      else if (place === 3) dancer.third++;
                      
                      if (place >= 1 && place <= 3) {
                        dancer.total++;
                      }
                    }
                  });
                }
              });
            } catch (error) {
              // Collection might not exist
            }
            
            // Check dance-specific competitions
            try {
              const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (comp.results && Array.isArray(comp.results)) {
                    comp.results.forEach((result) => {
                      if (result.studio === studio && result.dancerName) {
                        const dancerName = result.dancerName;
                        const place = result.place;
                        
                        if (!dancerMap.has(dancerName)) {
                          dancerMap.set(dancerName, {
                            name: dancerName,
                            first: 0,
                            second: 0,
                            third: 0,
                            total: 0,
                            competitions: new Set()
                          });
                        }
                        
                        const dancer = dancerMap.get(dancerName);
                        dancer.competitions.add(doc.id);
                        
                        if (place === 1) dancer.first++;
                        else if (place === 2) dancer.second++;
                        else if (place === 3) dancer.third++;
                        
                        if (place >= 1 && place <= 3) {
                          dancer.total++;
                        }
                      }
                    });
                  }
                });
              }
            } catch (error) {
              // Collection might not exist
            }
          }
        }
      } catch (error) {
        console.warn("Could not load from new structure:", error);
      }
      
      // Convert to array and sort by 1st places first, then 2nd places, then 3rd places
      const dancerArray = Array.from(dancerMap.values())
        .map(dancer => ({
          ...dancer,
          competitionCount: dancer.competitions.size
        }))
        // Filter out dancers without any podium finishes
        .filter(dancer => dancer.total > 0)
        .sort((a, b) => {
          // Sort by 1st places first
          if (b.first !== a.first) return b.first - a.first;
          // Then by 2nd places
          if (b.second !== a.second) return b.second - a.second;
          // Then by 3rd places
          if (b.third !== a.third) return b.third - a.third;
          // Finally by total if somehow they're still tied
          return b.total - a.total;
        });
      
      console.log("🏛️ Found", dancerArray.length, "dancers for studio:", studio);
      setDancers(dancerArray);
      
    } catch (err) {
      console.error("❌ Error loading studio dancers:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <h2>🏛️ {decodeURIComponent(studioName || '')}</h2>
        <p>Loading studio dancers...</p>
      </div>
    );
  }

  if (error) {
    const decodedStudioName = decodeURIComponent(studioName || '');
    const displayStudioName = studioProfileData?.displayName || decodedStudioName;
    
    return (
      <div style={{ padding: "2rem", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <h2>{displayStudioName}</h2>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  const decodedStudioName = decodeURIComponent(studioName || '');
  const displayStudioName = studioProfileData?.displayName || decodedStudioName;

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <h1>🏆 Hall of Fame</h1>
      <h2 style={{ color: "#666", marginBottom: "1rem" }}>
        {displayStudioName} {isProfileClaimed && (
          <span className="verified-badge" title="Verified Profile">
            ✅ Verified
          </span>
        )}
        {/* Visitor Count - Visible to everyone */}
        {visitorCount !== null && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginLeft: '1rem',
            padding: '0.25rem 0.75rem',
            backgroundColor: '#f0f0f0',
            borderRadius: '20px',
            fontSize: '0.85rem',
            color: '#666',
            fontWeight: 'normal'
          }}>
            <span>👁️</span>
            <span>{visitorCount.toLocaleString()} {visitorCount === 1 ? 'visitor' : 'visitors'}</span>
          </span>
        )}
      </h2>
      
      <div style={{ margin: "0 0 1rem" }}>
        <FollowButton type="studio" name={decodedStudioName} />
      </div>

      <div className="studio-profile-header">
        {/* Studio Logo */}
        <div className="studio-logo-container">
          {studioProfileData?.logoURL ? (
            <img
              key={studioProfileData.logoURL} // Force re-render when URL changes
              src={studioProfileData.logoURL}
              alt={`${displayStudioName} logo`}
              className="studio-logo-responsive"
              onLoad={() => {
                console.log(`✅ Studio logo loaded successfully for: ${displayStudioName}`);
              }}
              onError={(e) => {
                console.error(`❌ Failed to load studio logo for: ${displayStudioName}`, e);
              }}
            />
          ) : (
            <img
              src={`/images/studios/${encodeURIComponent(decodedStudioName)}.jpg`}
              alt={`${displayStudioName} logo`}
              className="studio-logo-responsive"
              onError={(e) => {
                // Try .png extension if .jpg fails
                if (e.target.src.endsWith('.jpg')) {
                  e.target.src = `/images/studios/${encodeURIComponent(decodedStudioName)}.png`;
                } else {
                  // If both fail, show placeholder
                  e.target.style.display = "none";
                  // Show placeholder div
                  const placeholder = document.createElement('div');
                  placeholder.className = 'studio-logo-placeholder';
                  placeholder.innerHTML = displayStudioName.charAt(0).toUpperCase();
                  e.target.parentNode.insertBefore(placeholder, e.target);
                }
              }}
              onLoad={() => {
                console.log(`✅ Studio logo loaded for: ${displayStudioName}`);
              }}
            />
          )}
        </div>
        {/* Studio Information */}
        {studioProfileData && (
          <div style={{ flex: 1 }}>
            {studioProfileData.yearEstablished && (
              <div style={{ marginBottom: "0.5rem", fontSize: "1.1rem", color: "#555" }}>
                <strong>Since {studioProfileData.yearEstablished}</strong>
              </div>
            )}
            
            {studioProfileData.description && (
              <div style={{ marginBottom: "1rem", color: "#666", lineHeight: "1.6" }}>
                {studioProfileData.description}
              </div>
            )}
            
            {studioProfileData.website && (
              <div style={{ marginBottom: "0.5rem" }}>
                <strong>🌐 Website:</strong>{' '}
                <a 
                  href={studioProfileData.website.startsWith('http') ? studioProfileData.website : `https://${studioProfileData.website}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#0066cc", textDecoration: "none" }}
                >
                  {studioProfileData.website}
                </a>
              </div>
            )}
            
            {/* Social Media Links */}
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              {studioProfileData.socialMedia?.instagram && (
                <a 
                  href={studioProfileData.socialMedia.instagram.startsWith('http') ? studioProfileData.socialMedia.instagram : `https://instagram.com/${studioProfileData.socialMedia.instagram.replace('@', '')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#E4405F", fontSize: "1.5rem", textDecoration: "none" }}
                  title="Instagram"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              )}
              {studioProfileData.socialMedia?.facebook && (
                <a 
                  href={normalizeFacebookUrl(studioProfileData.socialMedia.facebook)}
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#1877F2", fontSize: "1.5rem", textDecoration: "none" }}
                  title="Facebook"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              {studioProfileData.socialMedia?.tiktok && (
                <a 
                  href={studioProfileData.socialMedia.tiktok.startsWith('http') ? studioProfileData.socialMedia.tiktok : `https://tiktok.com/@${studioProfileData.socialMedia.tiktok.replace('@', '')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#000000", fontSize: "1.5rem", textDecoration: "none" }}
                  title="TikTok"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'dancers' ? 'active' : ''}`}
          onClick={() => setActiveTab('dancers')}
        >
          🏆 Hall of Fame
        </button>
        {user && isProfileClaimed && claimedBy?.userId === user.uid && (
          <button
            className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            ⚙️ Manage Studio Profile
          </button>
        )}
        {(!isProfileClaimed || hasPendingClaim) && (
          <button
            className={`tab ${activeTab === 'claim' ? 'active' : ''}`}
            onClick={() => setActiveTab('claim')}
          >
            🏢 Claim Studio Profile
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'dancers' && (
        <div>
          <p style={{ color: "#666", marginBottom: "2rem" }}>
            {dancers.length} dancers with podium finishes representing this studio.
          </p>

          {dancers.length > 0 ? (
            <div className="studio-dancers-table-container">
              <table className="studio-dancers-table">
                <thead>
                  <tr>
                    <th className="rank-column">Rank</th>
                    <th className="dancer-column">Dancer</th>
                    <th className="medal-column gold">🥇</th>
                    <th className="medal-column silver">🥈</th>
                    <th className="medal-column bronze">🥉</th>
                    <th className="desktop-only total-column">Total Podiums</th>
                    <th className="desktop-only competitions-column">Competitions</th>
                  </tr>
                </thead>
                <tbody>
                  {dancers.map((dancer, index) => (
                    <tr key={dancer.name} className={`dancer-row ${index < 3 ? 'podium-row' : ''}`}>
                      <td className="rank-column">
                        {index === 0 && "🥇"}
                        {index === 1 && "🥈"}
                        {index === 2 && "🥉"}
                        {index > 2 && `#${index + 1}`}
                      </td>
                      <td className="dancer-column">
                        <div className="dancer-info" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <DancerThumbnail 
                            dancerName={dancer.name} 
                            size={36} 
                            showPlaceholder={true}
                          />
                          <a 
                            href={`/dancer/${encodeURIComponent(dancer.name)}`}
                            className="dancer-link"
                          >
                            {dancer.name}
                          </a>
                        </div>
                      </td>
                      <td className="medal-column gold">
                        {dancer.first}
                      </td>
                      <td className="medal-column silver">
                        {dancer.second}
                      </td>
                      <td className="medal-column bronze">
                        {dancer.third}
                      </td>
                      <td className="desktop-only total-column">
                        {dancer.total}
                      </td>
                      <td className="desktop-only competitions-column">
                        {dancer.competitionCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ 
              textAlign: "center", 
              padding: "3rem", 
              color: "#666",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px"
            }}>
              <p>No dancers found with podium finishes for this studio.</p>
              <p style={{ fontSize: "0.9rem" }}>This studio may not have any recorded competition results yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Claim Studio Profile Tab */}
      {activeTab === 'claim' && (!isProfileClaimed || hasPendingClaim) && (
        <div>
          {user ? (
            <ClaimStudioProfile 
              studioName={decodedStudioName}
              onClaimSubmitted={() => {
                // Refresh pending claim status
                setHasPendingClaim(true);
                // Optionally reload profile data or switch back to dancers tab
                setActiveTab('dancers');
              }}
            />
          ) : (
            <LoginPrompt type="studio profile" />
          )}
        </div>
      )}

      {/* Manage Studio Profile Tab */}
      {activeTab === 'manage' && user && isProfileClaimed && claimedBy?.userId === user.uid && (
        <ManageStudioProfile 
          studioName={decodedStudioName}
          user={user}
          studioProfileData={studioProfileData}
          onProfileUpdated={() => {
            // Reload studio profile data
            loadStudioProfileData(decodedStudioName);
          }}
        />
      )}
    </div>
  );
}
